// @flow

import * as React from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Box, Flex } from 'react-system';
import { FormControlLabel, Checkbox, Divider } from '@material-ui/core';
import { Filter } from '../../../controls/Filter';
import { AnimExpandIcon } from '../../../controls/Filter/AnimExpandIcon';
import { useLocale } from '../../../hooks/locale';
import { useTheme } from '../../../theme';
import { createDescriptor } from '../../../controls/router';
import * as icons from '../../../icons';
import type { AggregatesListingsFilters_root } from '../__generated__/AggregatesListingsFilters_root.graphql.js';

import { Portals } from './PortalFilter';
import { AgencyFilter } from './AgencyFilter';
import { AgencyNameFilter } from './AgencyNameFilter';
import type { OtherFilterMeQuery } from './__generated__/OtherFilterMeQuery.graphql';

import { OnlineSinceFilter } from './OnlineSinceFilter';

import {
  offerTypeDescriptor,
  pageDescriptor,
  propertyTypeFilterDescriptor,
  equipmentFilterDescriptor,
  otherFilterDescriptor,
} from './sharedDescriptors';

import {
  getDescriptorsByPropertyMainType,
  getFiltersByPropertyMainType,
} from './filtersByPropertyMainType';

type Props = {|
  root: AggregatesListingsFilters_root,
  inline?: boolean,
  shouldFlush: boolean,
|};

const Estimate = (props: Props) => {
  return (
    <>
      <OnlineSinceFilter shouldFlush={props.shouldFlush} />
    </>
  );
};

const useMe = () => {
  const data = useLazyLoadQuery<OtherFilterMeQuery>(
    graphql`
      query OtherFilterMeQuery {
        me {
          isAdmin
        }
      }
    `,
    {},
  );

  return data.me;
};

const checkboxesFiltersDescriptor = createDescriptor(
  equipmentFilterDescriptor,
  offerTypeDescriptor,
  pageDescriptor,
);

const OtherFilterCheckbox = ({ value, onChange, children }) => {
  const { text } = useTheme();
  return (
    <Box width={[1, 1 / 2]}>
      <FormControlLabel
        control={
          <Checkbox
            color="primary"
            checked={value != null && value !== false}
            onChange={event => {
              if (event.target.checked) {
                onChange(true);
              } else {
                onChange(null);
              }
            }}
          />
        }
        label={<Box css={text.subtitle2}>{children}</Box>}
      />
    </Box>
  );
};

const Expand = ({ height, children, renderMore }) => {
  const [open, setOpen] = React.useState(false);
  const handleClick = () => {
    setOpen(v => v === false);
  };
  return (
    <>
      <div
        css={{ overflow: 'hidden' }}
        style={{ height: open ? 'auto' : height }}
      >
        {children}
      </div>
      {renderMore({ onClick: handleClick, open })}
    </>
  );
};

const Checkboxes = ({ shouldFlush }) => {
  const [equipment, setEquipment, flushEquipment] =
    checkboxesFiltersDescriptor.useState();

  const { t } = useLocale();

  const flush = () => {
    setEquipment({ page: 0 });
    if (shouldFlush) {
      flushEquipment();
    }
  };

  return (
    <Flex p={3} pb={0} flexWrap="wrap">
      <OtherFilterCheckbox
        value={equipment.isNewDevelopment_eq}
        onChange={value => {
          setEquipment({ isNewDevelopment_eq: value });
          flush();
        }}
      >
        {t('newDevelopment')}
      </OtherFilterCheckbox>
      <OtherFilterCheckbox
        value={equipment.hasBalcony_eq}
        onChange={value => {
          setEquipment({ hasBalcony_eq: value });
          flush();
        }}
      >
        {t('hasBalconyAuto')}
      </OtherFilterCheckbox>
      <OtherFilterCheckbox
        value={equipment.hasGarden_eq}
        onChange={value => {
          setEquipment({ hasGarden_eq: value });
          flush();
        }}
      >
        {t('hasGardenAuto')}
      </OtherFilterCheckbox>
      <OtherFilterCheckbox
        value={equipment.hasParking_eq}
        onChange={value => {
          setEquipment({ hasParking_eq: value });
          flush();
        }}
      >
        {t('hasParkingAuto')}
      </OtherFilterCheckbox>
      <OtherFilterCheckbox
        value={equipment.hasElevator_eq}
        onChange={value => {
          setEquipment({ hasElevator_eq: value });
          flush();
        }}
      >
        {t('hasElevator')}
      </OtherFilterCheckbox>
      <OtherFilterCheckbox
        value={equipment.hasView_eq}
        onChange={value => {
          setEquipment({ hasView_eq: value });
          flush();
        }}
      >
        {t('hasView')}
      </OtherFilterCheckbox>
      <OtherFilterCheckbox
        value={equipment.hasFireplace_eq}
        onChange={value => {
          setEquipment({ hasFireplace_eq: value });
          flush();
        }}
      >
        {t('hasFireplace')}
      </OtherFilterCheckbox>
      <OtherFilterCheckbox
        value={equipment.hasGarage_eq}
        onChange={value => {
          setEquipment({ hasGarage_eq: value });
          flush();
        }}
      >
        {t('hasGarage')}
      </OtherFilterCheckbox>
      <OtherFilterCheckbox
        value={equipment.isChildFriendly_eq}
        onChange={value => {
          setEquipment({ isChildFriendly_eq: value });
          flush();
        }}
      >
        {t('childFriendly')}
      </OtherFilterCheckbox>
      <OtherFilterCheckbox
        value={equipment.isWheelchairAccessible_eq}
        onChange={value => {
          setEquipment({ isWheelchairAccessible_eq: value });
          flush();
        }}
      >
        {t('wheelChairAccessible')}
      </OtherFilterCheckbox>
      <OtherFilterCheckbox
        value={equipment.isFurnished_eq}
        onChange={value => {
          setEquipment({ isFurnished_eq: value });
          flush();
        }}
      >
        {t('furnished')}
      </OtherFilterCheckbox>
      <OtherFilterCheckbox
        value={equipment.bookOnline_eq}
        onChange={value => {
          setEquipment({ bookOnline_eq: value });
          flush();
        }}
      >
        {t('bookOnline')}
      </OtherFilterCheckbox>
      <OtherFilterCheckbox
        value={equipment.isFsbo_eq}
        onChange={value => {
          setEquipment({ isFsbo_eq: value });
          flush();
        }}
      >
        {t('fsbo')}
      </OtherFilterCheckbox>
      <OtherFilterCheckbox
        value={equipment.isMinergieCertified_eq}
        onChange={value => {
          setEquipment({ isMinergieCertified_eq: value });
          flush();
        }}
      >
        {t('minergieCertified')}
      </OtherFilterCheckbox>
    </Flex>
  );
};

const OtherFilterInline = React.memo<Props>(props => {
  const [{ offerType }] = offerTypeDescriptor.useState();
  const { t } = useLocale();
  const { text, colors } = useTheme();
  const me = useMe();

  const [{ compositePropertyType_eq: propertyMainType }] =
    propertyTypeFilterDescriptor.useState();

  const expandRenderMore = ({ onClick, open }) => (
    <Flex
      css={{ color: colors.blue700 }}
      p={3}
      justifyContent="center"
      alignItems="center"
    >
      <Box css={[text.link]} onClick={onClick}>
        {open ? t('collapse') : t('showMore')}
      </Box>
      <AnimExpandIcon open={open} />
    </Flex>
  );

  return (
    <Box py={1}>
      {getFiltersByPropertyMainType(propertyMainType, 'all', true).map(
        (Filter, index) => (
          <React.Fragment key={index}>
            <Box p={3} mb={1}>
              <Filter
                inline={true}
                root={props.root}
                shouldFlush={props.shouldFlush}
              />
            </Box>
            <Divider />
          </React.Fragment>
        ),
      )}

      {offerType === 'buy' &&
        getFiltersByPropertyMainType(propertyMainType, 'buy', true).map(
          (Filter, index) => (
            <React.Fragment key={index}>
              <Box p={3} mb={1}>
                <Filter root={props.root} shouldFlush={props.shouldFlush} />
              </Box>
              <Divider />
            </React.Fragment>
          ),
        )}

      {offerType === 'rent' &&
        getFiltersByPropertyMainType(propertyMainType, 'rent', true).map(
          (Filter, index) => (
            <React.Fragment key={index}>
              <Box p={3} mb={1}>
                <Filter root={props.root} shouldFlush={props.shouldFlush} />
              </Box>
              <Divider />
            </React.Fragment>
          ),
        )}

      <Box p={3} pb={0} css={[text.subtitle1]}>
        {t('features')}
      </Box>
      <Expand renderMore={expandRenderMore} height={120}>
        <Checkboxes shouldFlush={props.shouldFlush} />
      </Expand>
      <Divider />
      <Flex p={3} pb={0} justifyContent="center" css={text.h6}>
        {t('advanced')}
      </Flex>
      <Estimate {...props} />
      {me?.isAdmin === true && (
        <>
          <Box p={3} pb={0} css={[text.subtitle1]}>
            {t('portals')}
          </Box>
          {props.root.aggregatesListingInfo.portals.length > 4 ? (
            <Expand renderMore={expandRenderMore} height={120}>
              <Portals root={props.root} shouldFlush={props.shouldFlush} />
            </Expand>
          ) : (
            <Portals root={props.root} shouldFlush={props.shouldFlush} />
          )}
          <Divider />
        </>
      )}
      <Box p={3} pb={0} css={[text.subtitle1]}>
        {t('searchByListingsReferenceId')}
      </Box>
      <AgencyFilter shouldFlush={props.shouldFlush} />
      {me?.isAdmin === true && (
        <>
          <Divider />
          <Box p={3} pb={0} css={[text.subtitle1]}>
            {t('searchByAgencyNames')}
          </Box>
          <AgencyNameFilter shouldFlush={props.shouldFlush} />
        </>
      )}
    </Box>
  );
});

export const OtherFilter = (props: Props): React.Node => {
  const [{ offerType }] = offerTypeDescriptor.useState();
  const [{ compositePropertyType_eq: propertyMainType }] =
    propertyTypeFilterDescriptor.useState();
  const combinedDescriptor = createDescriptor(
    otherFilterDescriptor,
    ...getDescriptorsByPropertyMainType(propertyMainType, 'all', true),
    ...getDescriptorsByPropertyMainType(propertyMainType, offerType, true),
  );
  const flushDescriptor = createDescriptor(
    otherFilterDescriptor,
    pageDescriptor,
    ...getDescriptorsByPropertyMainType(propertyMainType, 'all', true),
    ...getDescriptorsByPropertyMainType(propertyMainType, offerType, true),
  );
  const [, setEquipment, flushEquipment] = flushDescriptor.useState();
  const empty = combinedDescriptor.useIsEmpty();
  const { t } = useLocale();

  const flush = () => {
    setEquipment({ page: 0 });
    if (props.shouldFlush) {
      flushEquipment();
    }
  };

  const dialogTitle = t('otherFilters');

  return props.inline === true ? (
    <React.Suspense fallback={null}>
      <OtherFilterInline {...props} />
    </React.Suspense>
  ) : (
    <Filter
      label={dialogTitle}
      empty={empty}
      onReset={() => {
        setEquipment(combinedDescriptor.defaults);
        flush();
      }}
      Icon={icons.FilterList}
    >
      <Box
        width={'380px'}
        css={{
          maxHeight: 'calc(100vh - 260px)', // 260 = top bar + some free space
          overflowX: 'hidden',
        }}
      >
        <React.Suspense fallback={null}>
          <OtherFilterInline {...props} />
        </React.Suspense>
      </Box>
    </Filter>
  );
};
